/**
 * TASK-841 — a client name is never a UUID.
 *
 * `radar_service.py:596` used to be `client_name.get(client_id, client_id)`: the second
 * argument to `dict.get` is the DEFAULT, so a missed lookup wrote the KEY into the name
 * field. One live row rendered
 *
 *     Confirm document type: passport
 *     96c29963-d541-4d95-973f-e10049008f74
 *     CHF exposure not applicable · 1d
 *
 * at an advisor. The projection is fixed at source, and this is the second layer: one bad
 * record — from that path, a future one, or a hand-crafted payload — must not be able to put
 * an opaque id in front of someone. It is deliberately cheap and deliberately duplicated
 * effort.
 *
 * The check also matters beyond rendering. A UUID-shaped name DEFEATS the null test: the
 * value is non-null, so every "does this signal name its client" count scored such a row as
 * named. Anything asserting that property should use {@link namesAClient} rather than a
 * truthiness check.
 */

/**
 * Any RFC-4122 layout, not v4 alone. Client ids on this book are v5 (`17f37852-4006-5575-…`),
 * so the `4[0-9a-f]{3}-[89ab]` pattern used elsewhere in the codebase for session ids would
 * miss exactly the ids that reach these surfaces.
 */
const UUID_SHAPED = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Does this value actually name someone? Blank, absent, or an identifier does not. */
export const namesAClient = (
  name: string | null | undefined,
  clientId?: string | null,
): boolean => {
  const trimmed = name?.trim();
  if (!trimmed) return false;
  if (UUID_SHAPED.test(trimmed)) return false;
  // An id that is not UUID-shaped is still an id — this book also uses `DEMO-glody-ki-001`.
  // Only rejected when it is THIS row's own id, so a client genuinely called something that
  // happens to match another row's identifier is untouched.
  if (clientId && trimmed === clientId.trim()) return false;
  return true;
};

/**
 * What to render. `fallback` is the caller's translated `alerts.scope.client`, so this file
 * holds no copy of the string and TASK-820's `t(key, text)` rule stays with the component
 * that has the `t`.
 */
export const clientDisplayName = (
  name: string | null | undefined,
  clientId: string | null | undefined,
  fallback: string,
): string => (namesAClient(name, clientId) ? (name as string).trim() : fallback);
